/**
 * Copyright (c) 2019, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Linkify from 'react-linkify';
import { Link } from 'react-router-dom';
import FooterTimestamp from '../../../status2/components/FooterTimestamp';

// Styled Components Definitons START
const visibleNotification = `
  visibility: visible;
  opacity: 1;
  transition: opacity .1s linear,visibility .1s linear;
`;

const triangle = `
  &:before {
    left: auto;
    right: 1rem;
    top: -0.5rem;
    margin-right: -0.5rem;
    box-sizing: border-box;
    width: 1rem;
    height: 1rem;
    position: absolute;
    transform: rotate(45deg);
    content: '';
    background-color: inherit;
    top: -0.5rem;
    margin-left: -0.5rem;
  }
  &:after {
    left: auto;
    right: 1rem;
    top: -0.5rem;
    margin-right: -0.5rem;

    box-shadow: -1px -1px 0 0 rgba(0, 0, 0, 0.16);
    z-index: -1;


    width: 1rem;
    height: 1rem;
    position: absolute;
    transform: rotate(45deg);
    content: '';
    background-color: inherit;
    margin-left: -0.5rem;
  }
`;

const NotificationWrapper = styled.div`
  position: absolute;
  display: inline-block;
  padding: 0 0.75rem;
`;

const Notification = styled.div`
  max-height: 21.2rem;
  border: solid 1px #d8dde6;
  opacity: 0;
  transition: opacity 0.1s linear, visibility 0.1s linear;
  top: ${p => (p.top ? p.top : 'calc(100% + 0.5rem)')};
  right: ${p => (p.offsetRight ? p.offsetRight : 'calc(50% - 1.45rem)')};
  position: absolute;
  z-index: 7000;
  float: left;
  width: 20rem;
  margin-top: 0.125rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  background: #fff;
  box-shadow: 0 2px 6px 0 rgba(84, 105, 141, 0.5);
  ${visibleNotification}
  ${p => !p.hideTriangle && triangle}
  @media (max-width: 375px) {
    width: 80vw;
  }
`;

const NotificationHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #d8dde6;
`;

const Heading = styled.span`
  font-size: 1rem;
  line-height: 1.5;
  color: #16325c;
`;

const CloseButton = styled.span`
  font-size: 0.8125rem;
  line-height: 1.54;
  letter-spacing: normal;
  color: #006dcc;
  float: right;
  cursor: pointer;
`;

const ContentHeader = styled.div`
  font-size: 0.875rem;
  line-height: 1.5;
  color: #40607f;
  padding: 15px;
`;

const ContentBody = styled.div`
  font-size: 0.75rem;
  line-height: 1.33;
  color: #16325c;
  padding: 0 15px;
`;

const ContentFooter = styled(FooterTimestamp)`
  font-size: 0.75rem;
  line-height: 1.25;
  color: #54698d;
  padding: 0 15px;
`;

const NotificationFooter = styled.div`
  height: 3rem;
  border-radius: 4px;
  background-color: #f4f6f9;
`;

const NotificationFooterLink = styled(Link)`
  font-size: 0.8125rem;
  line-height: 3rem;
  color: #006dcc;
  padding-left: 15px;
`;

const NoContentMessage = styled.div`
  padding: 15px 15px 0 15px;
  font-size: 0.75rem;
  line-height: 1.33;
  color: #16325c;
`;

const ContentItems = styled.div`
  max-height: 233px;
  overflow-y: auto;
  padding-bottom: 1.5rem;
  box-shadow: inset 0 -1px 0 0 #d8dde6;
`;

const ContentItem = styled.div``;
// Styled Components Definitons END

/**
 * NotificationDropdown component
 * This component displays messages in a dropDown in an icon.
 * This can be used by the NotificationIcon component to render a popover in an icon.
 */
const NotificationDropdown = ({
  heading,
  contentValues,
  offsetRight,
  hideTriangle,
  top,
  style,
  showNotificationCount,
  notificationCount,
  closeButtonText,
  noContentMessage,
  footerText,
  footerLink,
  onCloseButtonClick
}) => {
  const onNotificationDropdownCloseButtonClick = () => {
    onCloseButtonClick();
  };
  return (
    <NotificationWrapper tabindex="0">
      <Notification
        top={top}
        offsetRight={offsetRight}
        style={style}
        hideTriangle={hideTriangle}
      >
        <NotificationHeader>
          {heading && (
            <Heading>
              {heading}
              {showNotificationCount &&
                notificationCount !== 0 &&
                ` (${notificationCount})`}
            </Heading>
          )}
          {closeButtonText && (
            <CloseButton onClick={onNotificationDropdownCloseButtonClick}>
              {closeButtonText}
            </CloseButton>
          )}
        </NotificationHeader>
        <ContentItems>
          {contentValues.length !== 0 &&
            contentValues.map(option => {
              return (
                <ContentItem key={option.subject} className={option.classname}>
                  <ContentHeader>{option.subject}</ContentHeader>
                  <Linkify properties={{ target: '_blank' }}>
                    <ContentBody>{option.content}</ContentBody>
                  </Linkify>
                  <ContentFooter
                    createdTime={option.createdTime}
                    region={option.region}
                  />
                </ContentItem>
              );
            })}
          {contentValues.length === 0 && (
            <NoContentMessage>{noContentMessage}</NoContentMessage>
          )}
        </ContentItems>
        <NotificationFooter>
          <NotificationFooterLink to={footerLink}>
            {footerText}
          </NotificationFooterLink>
        </NotificationFooter>
      </Notification>
    </NotificationWrapper>
  );
};

// heading, closeButtonText, noContentMessage, footerText are React Elements (<Localized>),
// and therfore need to have object propType

NotificationDropdown.propTypes = {
  heading: PropTypes.object,
  contentValues: PropTypes.arrayOf(PropTypes.object),
  offsetRight: PropTypes.object,
  hideTriangle: PropTypes.bool,
  top: PropTypes.number,
  style: PropTypes.string,
  showNotificationCount: PropTypes.bool,
  notificationCount: PropTypes.number,
  closeButtonText: PropTypes.object,
  noContentMessage: PropTypes.object,
  footerText: PropTypes.object,
  footerLink: PropTypes.string,
  onCloseButtonClick: PropTypes.func
};

export default NotificationDropdown;
